import { Dialog as DialogPrimitive } from '@base-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react'

import { IconButton } from '@/components/ui/IconButton'
import { cn } from '@/lib/cn'
import {
  scrimVariants,
  sheetDesktopVariants,
  sheetMobileVariants,
} from '@/lib/motion'

/* The sheet is the one component whose motion genuinely differs between
 * breakpoints, and the difference cannot be expressed in class names: on touch
 * it is anchored to the bottom edge and rises from it, while on desktop it is a
 * centred dialog anchored to nothing, which can only fade and lift.
 *
 * Tailwind's `sm:` prefix can swap a class but not a variant object, so the
 * breakpoint has to be read in JS. useSyncExternalStore subscribes to the media
 * query itself rather than to a resize listener, so it re-renders only when the
 * breakpoint is actually crossed, and it reads the right value on the FIRST
 * paint — a useEffect-based check would animate the first sheet of a session
 * with the wrong variant. 40rem is Tailwind's default `sm`. */
const DESKTOP_QUERY = '(min-width: 40rem)'

function subscribeToBreakpoint(onChange: () => void) {
  const query = window.matchMedia(DESKTOP_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function useIsDesktop() {
  return useSyncExternalStore(
    subscribeToBreakpoint,
    () => window.matchMedia(DESKTOP_QUERY).matches,
    // Prerender fallback; mobile-first, like the rest of the system.
    () => false,
  )
}

export interface SheetProps {
  open: boolean
  onClose: () => void
  /** Names the record being edited, e.g. "Cost line 2". */
  title: string
  children: ReactNode
  /** Full-width primary action, per the mobile editing primitive. */
  footer?: ReactNode
  /* Desktop width only — on touch a sheet is always full-bleed, so this changes
     nothing below `sm`. The default is the line editor the sheet was built for:
     stacked fields read badly past a certain measure. `wide` is for the one
     other thing a sheet is asked to hold — a DOCUMENT, whose tables were laid
     out for a page and which a 32rem column squeezes into wrapping. */
  size?: 'default' | 'wide'
}

const SIZE_CLASSES: Record<NonNullable<SheetProps['size']>, string> = {
  default: 'sm:max-w-lg',
  wide: 'sm:max-w-3xl lg:max-w-4xl',
}

/* Base UI stamps this on the `<div>` every FloatingPortal appends to its
   container — the dialog's own, and every popover, combobox and select popup
   that portals to `<body>`. It is how `startedInAnotherPortal` below tells
   "somebody clicked the page behind the sheet" from "somebody clicked inside a
   control the sheet itself opened". */
const PORTAL_ATTRIBUTE = '[data-base-ui-portal]'

/** The element a dismissal actually came from.
 *
 *  For a press that is the target. For a focus-out it is where focus WENT —
 *  `relatedTarget` — because the target is the element being left, which is
 *  inside the sheet by definition and would answer the wrong question. */
function originOf(event: Event | undefined): Element | null {
  if (!event) return null
  const related = (event as FocusEvent).relatedTarget
  if (related instanceof Element) return related
  return event.target instanceof Element ? event.target : null
}

/* The mobile editing primitive for any line item: one line, one sheet. Grabber,
 * a title naming the record, stacked fields, full-width primary action.
 *
 * On desktop the same component renders as a centred dialog, so a line editor
 * has one implementation rather than two. This is the one place in the system
 * allowed a strong shadow, because it must read as a layer above a live screen.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'default',
}: SheetProps) {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()

  /* 20px gutters are right for stacked fields and mean for a document: the wider
     the panel, the more the content needs a margin to read as a page rather than
     as text pressed against a frame. Applied to all three bands together so the
     header rule, the body and the footer stay on one vertical line. */
  const gutter = size === 'wide' ? 'sm:px-8' : undefined

  const actionsRef = useRef<DialogPrimitive.Root.Actions>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  /* THE PORTALLED-POPUP RULE, for the KEYBOARD half. The pointer half is
   * `disablePointerDismissal` on the Root below; read that first.
   *
   * A DatePicker's calendar, a Combobox's list and a Select's listbox all
   * portal to `<body>`, so every one of them is a DOM SIBLING of this sheet
   * rather than a descendant — and from the dialog's point of view an Escape
   * pressed inside one of them happens outside itself. Left alone, the Escape
   * that closes a calendar would close the sheet under it and lose the draft.
   *
   * So the dismissal is filtered by where it STARTED: an interaction whose
   * origin sits inside a portal that is not this sheet's own is a control the
   * sheet itself opened, and it is not a request to close anything. Written
   * against the portal marker rather than against a list of components, because
   * the next control to portal must not have to be added here. */
  const startedInAnotherPortal = (event: Event | undefined) => {
    const origin = originOf(event)
    const portal = origin?.closest(PORTAL_ATTRIBUTE)
    return Boolean(portal) && !portal?.contains(popupRef.current)
  }

  /* The scroll lock stays ours. `modal={false}` deliberately declines Base UI's
     modality — see the Popup below — and the page behind an open sheet must
     still not scroll under it. Restoring the previous value rather than
     clearing it keeps a second, outer lock intact. */
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  return (
    /* `modal={false}`, and it is load-bearing. With `true` or `'trap-focus'`
       Base UI wraps the rest of the page in `<div aria-hidden="true"
       data-base-ui-inert>`, and the FormSheet suites query page content while a
       sheet is open — every one of those lookups would stop finding anything.
       It also matches what this component has always done: the page behind was
       reachable, and the scrim below is what closes it. */
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next, details) => {
        if (next) return
        if (startedInAnotherPortal(details.event)) {
          // Base UI's own cancel, not a bare early return: without it the
          // primitive proceeds with its half of the dismissal and the sheet
          // ends up closed in the DOM while `open` still says otherwise.
          details.cancel()
          return
        }
        onClose()
      }}
      modal={false}
      /* NO POINTER DISMISSAL, and this is the fix for a bug that survived one
         round of guessing — write down the mechanism before touching it.

         The failure: open a leg sheet, open the ETD calendar, click a day. The
         date applied and the SHEET CLOSED, later asking to discard the draft.

         Why. For a non-modal dialog Base UI resolves `outsidePressEvent` to
         `'intentional'` for a mouse (dialog/root/useDialogRoot.mjs), which
         dismisses on CLICK rather than on pointerdown. The calendar commits and
         closes on `pointerdown`, so by the time the click lands its day cell —
         and the whole portal around it — is out of the DOM. The click retargets
         to whatever still exists under the cursor, which is not inside any
         portal, and the guard above therefore sees nothing to filter. No
         origin-sniffing can fix this: the origin is gone.

         Why declining the whole behaviour is right rather than a workaround.
         The scrim below covers the viewport and is a real, labelled button that
         closes the sheet — so a press that reaches the page BEHIND the sheet
         does not exist, and the primitive's outside-press has nothing left to
         do but misfire on debris like the click above. Escape is unaffected
         (`escapeKey` is a separate concern in the same hook), and every other
         way out — scrim, the header X, the footer Cancel — is explicit and
         calls `onClose` itself. */
      disablePointerDismissal
      actionsRef={actionsRef}
    >
      {/* keepMounted + AnimatePresence: the portal stays so framer can play the
          exit transition, and the imperative unmount below tears it down once
          that transition reports complete. Without it the panel would vanish on
          a single frame, which reads as a glitch rather than as a close. */}
      <DialogPrimitive.Portal keepMounted>
        <AnimatePresence onExitComplete={() => actionsRef.current?.unmount()}>
          {open ? (
            <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
              {/* Still a real button, not Dialog.Backdrop: it is the labelled
                  "Close" affordance a pointer user actually presses, and
                  demoting it to a decorative backdrop would remove it from the
                  accessibility tree. */}
              <motion.button
                type="button"
                aria-label={t('common.actions.close')}
                onClick={onClose}
                variants={scrimVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute inset-0 bg-[var(--overlay-scrim)]"
              />

              <DialogPrimitive.Popup
                ref={popupRef}
                aria-label={title}
                render={
                  <motion.div
                    variants={isDesktop ? sheetDesktopVariants : sheetMobileVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  />
                }
                className={cn(
                  'bg-surface relative flex max-h-[90dvh] w-full flex-col outline-none',
                  'rounded-t-[var(--radius-sheet)] shadow-[var(--shadow-sheet)]',
                  'sm:rounded-[var(--radius-lg)]',
                  'sm:shadow-[var(--shadow-overlay)]',
                  /* A wide sheet is only wide while the viewport allows it;
                     without the inset it would touch both edges on a small
                     laptop. */
                  'sm:mx-4',
                  SIZE_CLASSES[size],
                )}
              >
                {/* The grabber reads as "drag me" on touch and is decorative elsewhere. */}
                <div className="flex justify-center pt-2.5 sm:hidden">
                  <span aria-hidden className="bg-line h-1 w-[34px] rounded-full" />
                </div>

                <div
                  className={cn(
                    'flex items-start justify-between gap-3 px-5 pt-4 pb-3',
                    gutter,
                  )}
                >
                  <h2 className="text-text-heading text-[15px] leading-snug font-normal">
                    {title}
                  </h2>
                  <IconButton
                    icon={X}
                    label={t('common.actions.close')}
                    size="sm"
                    onClick={onClose}
                  />
                </div>

                <div
                  className={cn(
                    'asa-scroll flex-1 overflow-y-auto px-5 pb-4',
                    gutter,
                    size === 'wide' && 'sm:pb-6',
                  )}
                >
                  {children}
                </div>

                {footer ? (
                  <div
                    className={cn(
                      'border-line flex flex-col gap-2 border-t px-5 py-3.5',
                      gutter,
                    )}
                  >
                    {footer}
                  </div>
                ) : null}
              </DialogPrimitive.Popup>
            </div>
          ) : null}
        </AnimatePresence>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
